import type { LoopVoice, Out } from "../engine.ts";
import { TRIM } from "../levels.ts";
import { bank, bell, click, cymbal, gainOut, hat, kick, pluck, snare, sub, thump, tone } from "../synth.ts";

const D4 = 293.66;
const hz = (semi: number, base = D4) => base * 2 ** (semi / 12);

/**
 * Continuous voices. Each builder wires sources into `o.dest` (the engine owns the fade in/out
 * gain) and returns how to stop it, plus an optional 0..1 control.
 */

type Build = (o: Out) => LoopVoice;

const trimmed = (o: Out, db: number) => (db === 0 ? o : gainOut(o, 10 ** (db / 20)));

const gain = (ctx: BaseAudioContext, v: number) => {
  const n = ctx.createGain();
  n.gain.value = v;
  return n;
};
const filt = (ctx: BaseAudioContext, type: BiquadFilterType, f: number, q = 0.7) => {
  const n = ctx.createBiquadFilter();
  n.type = type;
  n.frequency.value = f;
  n.Q.value = q;
  return n;
};
const osc = (ctx: BaseAudioContext, type: OscillatorType, f: number) => {
  const n = ctx.createOscillator();
  n.type = type;
  n.frequency.value = f;
  return n;
};
const noiseLoop = (ctx: BaseAudioContext, color: "white" | "pink" | "brown") => {
  const n = ctx.createBufferSource();
  n.buffer = bank(ctx)[color];
  n.loop = true;
  return n;
};
/** An LFO wobbling `target` by ±depth. */
const lfo = (ctx: BaseAudioContext, rate: number, depth: number, target: AudioParam, type: OscillatorType = "sine") => {
  const l = osc(ctx, type, rate);
  l.connect(gain(ctx, depth)).connect(target);
  return l;
};

function start(o: Out, srcs: AudioScheduledSourceNode[]) {
  for (const s of srcs) {
    if (s instanceof AudioBufferSourceNode) s.start(o.t, Math.random() * 1.8);
    else s.start(o.t);
  }
  return (t: number) => {
    for (const s of srcs) s.stop(t);
  };
}

/**
 * Repeating events (heartbeats, sonar, drum patterns). Live contexts schedule ahead on a timer;
 * offline renders (the audio lab) pre-schedule `horizon` seconds.
 */
function pulse(o: Out, period: () => number, hit: (at: number, n: number) => void, horizon = 10) {
  let next = o.t;
  let n = 0;
  let stopAt = Number.POSITIVE_INFINITY;
  if (typeof OfflineAudioContext !== "undefined" && o.ctx instanceof OfflineAudioContext) {
    // Offline: pre-schedule, but after the caller has had a chance to set() the loop's control.
    queueMicrotask(() => {
      while (next < o.t + horizon) {
        hit(next, n++);
        next += period();
      }
    });
    return { stop: () => {} };
  }
  const tick = () => {
    const ahead = o.ctx.currentTime + 0.15;
    while (next < ahead && next < stopAt) {
      hit(next, n++);
      next += period();
    }
  };
  tick();
  const timer = setInterval(tick, 40);
  return {
    stop(t: number) {
      stopAt = t;
      clearInterval(timer);
    },
  };
}

// ------------------------------------------------------------------ hero loops

/** Jet thrust: brown-noise roar with a combustion flutter and a hiss on top. */
export const jets: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.jets);
  const { ctx } = o;
  const roar = noiseLoop(ctx, "brown");
  const hiss = noiseLoop(ctx, "white");
  const body = gain(ctx, 0.75);
  roar.connect(filt(ctx, "bandpass", 620, 0.7)).connect(body).connect(o.dest);
  hiss.connect(filt(ctx, "highpass", 3800)).connect(gain(ctx, 0.05)).connect(o.dest);
  const flutter = lfo(ctx, 27, 0.25, body.gain);
  return { stop: start(o, [roar, hiss, flutter]) };
};

/** Ward: a low D pedal with a slow shimmer; crackles more as it weakens (set = hp fraction). */
export const ward: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.ward);
  const { ctx } = o;
  const a = osc(ctx, "sine", hz(-24));
  const b = osc(ctx, "sine", hz(-12));
  a.connect(gain(ctx, 0.22)).connect(o.dest);
  b.connect(gain(ctx, 0.08)).connect(o.dest);
  const shimmer = osc(ctx, "sine", hz(18));
  const sg = gain(ctx, 0.02);
  shimmer.connect(sg).connect(o.dest);
  const sw = lfo(ctx, 0.35, 0.015, sg.gain);
  const crackle = noiseLoop(ctx, "pink");
  const cg = gain(ctx, 0.01);
  crackle.connect(filt(ctx, "bandpass", 2200, 3)).connect(cg).connect(o.dest);
  return {
    stop: start(o, [a, b, shimmer, sw, crackle]),
    set: (v, t) => cg.gain.setTargetAtTime(0.01 + (1 - v) * 0.08, t, 0.1),
  };
};

/** Take a Breather: ragged breathing through a respirator, a warm heal hum underneath. */
export const breather: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.breather);
  const { ctx } = o;
  const air = noiseLoop(ctx, "pink");
  const ag = gain(ctx, 0.14);
  air.connect(filt(ctx, "bandpass", 1150, 1.6)).connect(ag).connect(o.dest);
  const breath = lfo(ctx, 0.9, 0.12, ag.gain);
  const hum = osc(ctx, "sine", hz(-12));
  hum.connect(gain(ctx, 0.05)).connect(o.dest);
  return { stop: start(o, [air, breath, hum]) };
};

/** Levitation: cloth fluttering in an updraft. */
export const levitate: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.levitate);
  const { ctx } = o;
  const air = noiseLoop(ctx, "pink");
  const ag = gain(ctx, 0.12);
  air.connect(filt(ctx, "bandpass", 520, 1.4)).connect(ag).connect(o.dest);
  const flap = lfo(ctx, 6, 0.06, ag.gain, "triangle");
  const tone1 = osc(ctx, "sine", hz(18));
  tone1.connect(gain(ctx, 0.008)).connect(o.dest);
  return { stop: start(o, [air, flap, tone1]) };
};

/** Wall Scamper: claws scrabbling up steel. */
export const climb: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.climb);
  const p = pulse(
    o,
    () => 0.09 + Math.random() * 0.05,
    (at) => click({ ...o, t: at }, 0.08 + Math.random() * 0.05, 3000 + Math.random() * 2500, 0.003),
  );
  return { stop: (t) => p.stop(t) };
};

/** Whole Hog: a scrap-metal motor. */
export const motor: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.motor);
  const { ctx } = o;
  const a = osc(ctx, "sawtooth", 62);
  const b = osc(ctx, "square", 124);
  const body = gain(ctx, 0.1);
  const lp = filt(ctx, "lowpass", 900, 2);
  a.connect(lp);
  b.connect(gain(ctx, 0.5)).connect(lp);
  lp.connect(body).connect(o.dest);
  const chug = lfo(ctx, 18, 0.05, body.gain, "square");
  return { stop: start(o, [a, b, chug]) };
};

/** Barrage: hovering jets locked in place, thrumming. */
export const barrage: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.barrage);
  const { ctx } = o;
  const roar = noiseLoop(ctx, "brown");
  const body = gain(ctx, 0.5);
  roar.connect(filt(ctx, "lowpass", 520)).connect(body).connect(o.dest);
  const thrum = lfo(ctx, 12, 0.2, body.gain);
  const low = osc(ctx, "sine", 55);
  low.connect(gain(ctx, 0.15)).connect(o.dest);
  return { stop: start(o, [roar, thrum, low]) };
};

/** Tactical Visor: a quiet radar sweep while it's up. */
export const visor: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.visor);
  const p = pulse(
    o,
    () => 0.36,
    (at) => tone({ ...o, t: at }, { f: 2640, gain: 0.03, d: 0.03 }),
  );
  const hum = osc(o.ctx, "sine", hz(19 + 12));
  hum.connect(gain(o.ctx, 0.004)).connect(o.dest);
  const stopHum = start(o, [hum]);
  return {
    stop: (t) => {
      p.stop(t);
      stopHum(t);
    },
  };
};

// ------------------------------------------------------------------ zones & projectiles

/** Biotic Field: a healing aura — the D major triad, gently trembling. */
export const bioticField: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.bioticField);
  const { ctx } = o;
  const out = gain(ctx, 1);
  out.connect(o.dest);
  const srcs: AudioScheduledSourceNode[] = [lfo(ctx, 5, 0.35, out.gain)];
  for (const s of [0, 4, 7]) {
    const n = osc(ctx, "sine", hz(s));
    n.connect(gain(ctx, 0.035)).connect(out);
    srcs.push(n);
  }
  const air = noiseLoop(ctx, "pink");
  air.connect(filt(ctx, "bandpass", 3200, 1)).connect(gain(ctx, 0.012)).connect(out);
  srcs.push(air);
  return { stop: start(o, srcs) };
};

/** Gateway portal: a swirling vortex on the Lydian tritone. */
export const portal: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.portal);
  const { ctx } = o;
  const air = noiseLoop(ctx, "pink");
  const bp = filt(ctx, "bandpass", 900, 4);
  air.connect(bp).connect(gain(ctx, 0.22)).connect(o.dest);
  const swirl = lfo(ctx, 0.7, 420, bp.frequency);
  const a = osc(ctx, "sine", hz(6));
  const b = osc(ctx, "sine", hz(7));
  a.connect(gain(ctx, 0.015)).connect(o.dest);
  b.connect(gain(ctx, 0.015)).connect(o.dest);
  return { stop: start(o, [air, swirl, a, b]) };
};

/** Rebirth Beacon: a sonar ping in the heal register. */
export const beacon: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.beacon);
  const p = pulse(
    o,
    () => 1.3,
    (at) => {
      bell({ ...o, t: at }, hz(24), 0.1, 0.35);
      tone({ ...o, t: at }, { f: hz(12), gain: 0.02, d: 0.3 });
    },
  );
  const hum = osc(o.ctx, "sine", hz(-12));
  hum.connect(gain(o.ctx, 0.02)).connect(o.dest);
  const stopHum = start(o, [hum]);
  return {
    stop: (t) => {
      p.stop(t);
      stopHum(t);
    },
  };
};

/** Amplifier: a bass pulse and a glint every half second. */
export const amplifier: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.amplifier);
  const { ctx } = o;
  const bass = osc(ctx, "sine", hz(-24));
  const bg = gain(ctx, 0.16);
  bass.connect(bg).connect(o.dest);
  const throb = lfo(ctx, 2, 0.12, bg.gain);
  const saw = osc(ctx, "sawtooth", hz(-12));
  saw.connect(filt(ctx, "lowpass", 800)).connect(gain(ctx, 0.025)).connect(o.dest);
  const stopSrc = start(o, [bass, throb, saw]);
  const p = pulse(
    o,
    () => 0.5,
    (at) => bell({ ...o, t: at }, hz(19), 0.08, 0.25),
  );
  return {
    stop: (t) => {
      stopSrc(t);
      p.stop(t);
    },
  };
};

/** A projectile in flight. `kind` picks the colour: rocket roar, missile hiss, orb hum. */
export const flight =
  (kind: "rocket" | "missile" | "orb" | "hook"): Build =>
  (raw) => {
    const o = trimmed(raw, TRIM.loop[kind]);
    const { ctx } = o;
    if (kind === "orb") {
      const a = osc(ctx, "sine", 600);
      const ag = gain(ctx, 0.04);
      a.connect(ag).connect(o.dest);
      const trem = lfo(ctx, 9, 0.025, ag.gain);
      return { stop: start(o, [a, trem]) };
    }
    const air = noiseLoop(ctx, kind === "rocket" ? "pink" : "white");
    const f = kind === "rocket" ? 850 : kind === "missile" ? 1500 : 2400;
    air.connect(filt(ctx, "bandpass", f, kind === "hook" ? 3 : 0.9)).connect(gain(ctx, kind === "rocket" ? 0.4 : 0.22)).connect(o.dest);
    const srcs: AudioScheduledSourceNode[] = [air];
    if (kind === "rocket") {
      const burn = noiseLoop(ctx, "brown");
      burn.connect(filt(ctx, "lowpass", 300)).connect(gain(ctx, 0.35)).connect(o.dest);
      srcs.push(burn);
    }
    return { stop: start(o, srcs) };
  };

// ------------------------------------------------------------------ player state

/** Heartbeat for low health. set = urgency 0..1 (70 → 130 bpm). */
export const heartbeat: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.heartbeat);
  let urgency = 0;
  const p = pulse(
    o,
    () => 60 / (70 + 60 * urgency),
    (at) => {
      thump({ ...o, t: at }, 0.55, 62, 40, 0.13);
      thump({ ...o, t: at + 0.19 }, 0.38, 56, 38, 0.12);
    },
  );
  return {
    stop: (t) => p.stop(t),
    set: (v) => {
      urgency = v;
    },
  };
};

// ------------------------------------------------------------------ objective & match

/**
 * Capture pulse. Ours is a D pedal that brightens and speeds up as the capture fills; the
 * fifth enters at half. Theirs grinds the flat second against the tonic.
 */
export const capture =
  (ours: boolean): Build =>
  (raw) => {
    const o = trimmed(raw, ours ? TRIM.loop.captureOurs : TRIM.loop.captureTheirs);
    const { ctx } = o;
    const out = gain(ctx, 0.6);
    out.connect(o.dest);
    const lp = filt(ctx, ours ? "lowpass" : "bandpass", ours ? 500 : 1100, ours ? 3 : 2);
    lp.connect(out);
    const tonic = osc(ctx, ours ? "sawtooth" : "square", hz(0));
    const color = osc(ctx, ours ? "sawtooth" : "square", hz(ours ? 7 : 1));
    const cg = gain(ctx, ours ? 0 : 0.5);
    tonic.connect(gain(ctx, 0.05)).connect(lp);
    color.connect(cg).connect(gain(ctx, 0.05)).connect(lp);
    const am = osc(ctx, "square", ours ? 3 : 4);
    const amg = gain(ctx, 0.35);
    am.connect(amg).connect(out.gain);
    return {
      stop: start(o, [tonic, color, am]),
      set: (v, t) => {
        am.frequency.setTargetAtTime((ours ? 3 : 4) + v * (ours ? 6 : 8), t, 0.2);
        if (ours) {
          lp.frequency.setTargetAtTime(500 + v * 3200, t, 0.2);
          cg.gain.setTargetAtTime(v > 0.5 ? 0.6 : 0, t, 0.1);
        }
      },
    };
  };

/** Both teams on the point: a tight, trembling cluster. */
export const contested: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.contested);
  const { ctx } = o;
  const out = gain(ctx, 0.6);
  out.connect(o.dest);
  const srcs: AudioScheduledSourceNode[] = [lfo(ctx, 11, 0.4, out.gain, "triangle")];
  for (const s of [0, 1, 7]) {
    const n = osc(ctx, "triangle", hz(s));
    n.connect(gain(ctx, 0.03)).connect(out);
    srcs.push(n);
  }
  return { stop: start(o, srcs) };
};

/**
 * The objective itself, heard in the world: a neutral open fifth, plus a third that tells you
 * who owns it by ear. set: 0 = nobody, 0.5 = us, 1 = them.
 */
export const pointHum: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.pointHum);
  const { ctx } = o;
  const srcs: AudioScheduledSourceNode[] = [];
  const voice = (semi: number, level: number, type: OscillatorType = "sine") => {
    const n = osc(ctx, type, hz(semi));
    const g = gain(ctx, level);
    n.connect(g).connect(o.dest);
    srcs.push(n);
    return g;
  };
  voice(-24, 0.12);
  voice(-17, 0.07);
  const ours = voice(-8, 0, "triangle");
  const theirs = voice(-23, 0, "sawtooth");
  const theirsTone = filt(ctx, "lowpass", 500);
  theirs.disconnect();
  theirs.connect(theirsTone).connect(o.dest);
  const air = noiseLoop(ctx, "pink");
  air.connect(filt(ctx, "bandpass", 180, 1)).connect(gain(ctx, 0.05)).connect(o.dest);
  srcs.push(air);
  return {
    stop: start(o, srcs),
    set: (v, t) => {
      ours.gain.setTargetAtTime(v > 0.25 && v < 0.75 ? 0.05 : 0, t, 0.3);
      theirs.gain.setTargetAtTime(v >= 0.75 ? 0.03 : 0, t, 0.3);
    },
  };
};

/**
 * The payload, heard in the world. It's the objective, so it idles on the neutral open fifth: a
 * diesel low D and A that chugs faster and opens up as it's pushed, with rail-joint clacks at
 * speed. set: 0 = standing still, 1 = a full three-player push.
 */
export const payload: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.payload);
  const { ctx } = o;
  const out = gain(ctx, 0.7);
  out.connect(o.dest);
  const lp = filt(ctx, "lowpass", 220, 2);
  lp.connect(out);
  const root = osc(ctx, "sawtooth", hz(-36));
  const fifth = osc(ctx, "sawtooth", hz(-29));
  root.connect(gain(ctx, 0.12)).connect(lp);
  fifth.connect(gain(ctx, 0.06)).connect(lp);
  const chug = osc(ctx, "square", 6);
  chug.connect(gain(ctx, 0.25)).connect(out.gain);
  const air = noiseLoop(ctx, "brown");
  const airLevel = gain(ctx, 0);
  air.connect(filt(ctx, "bandpass", 420, 0.8)).connect(airLevel).connect(out);
  let speed = 0;
  const clacks = pulse(
    o,
    () => (speed > 0.05 ? 0.9 / (0.4 + speed) : 0.25),
    (at) => {
      if (speed <= 0.05) return;
      click({ ...o, t: at }, 0.12 * speed, 2400, 0.012);
      thump({ ...o, t: at }, 0.1 * speed, 120, 70, 0.06);
      click({ ...o, t: at + 0.11 }, 0.09 * speed, 2000, 0.01);
    },
  );
  const stop = start(o, [root, fifth, chug, air]);
  return {
    stop: (t) => {
      stop(t);
      clacks.stop(t);
    },
    set: (v, t) => {
      speed = v;
      // Moving is news: up to ~3 dB over idle at a full push, and brighter.
      out.gain.setTargetAtTime(0.7 * (1 + 0.45 * v), t, 0.3);
      chug.frequency.setTargetAtTime(6 + v * 8, t, 0.3);
      lp.frequency.setTargetAtTime(220 + v * 700, t, 0.3);
      airLevel.gain.setTargetAtTime(v * 0.08, t, 0.3);
    },
  };
};

/**
 * Overtime: 140 bpm, four on the floor, a Phrygian 16th-note ostinato and a D pedal.
 * set = intensity (how close anyone is to winning): opens the hats and adds crashes.
 * It's dense (~40 notes a second), so the groove and the hats are baked into two seamless
 * loops the first time overtime happens; until then it plays live.
 */
const OT_STEP = 60 / 140 / 4;
const OT_BAR = 16 * OT_STEP;
const OT_RIFF = [0, 0, 1, 0, 3, 0, 1, 0, 0, 0, 1, 0, 5, 3, 1, 0];

function overtimeStep(o: Out, n: number, layer: "groove" | "hats", hatVel = 0.8) {
  const s = n % 16;
  if (layer === "hats") {
    if (s % 2 === 1) hat(o, hatVel);
    return;
  }
  if (s % 4 === 0) kick(o, 0.8);
  if (s === 4 || s === 12) snare(o, 0.55);
  pluck(o, hz(OT_RIFF[s]! - 12), OT_STEP * 0.9, 0.55 + (s % 4 === 0 ? 0.2 : 0));
  if (s === 0 || s === 8) sub(o, hz(-24), OT_STEP * 3, 0.6);
}

interface OvertimeBars {
  groove: AudioBuffer;
  hats: AudioBuffer;
}
let otBars: OvertimeBars | undefined;
let otBaking: Promise<void> | undefined;

/** Render bars 0..N and keep 1..N, so the loop starts with exactly the tails its end spills. */
export function warmOvertime(sampleRate: number) {
  otBaking ??= (async () => {
    const bars = 2;
    const layer = async (which: "groove" | "hats") => {
      const ctx = new OfflineAudioContext(1, Math.ceil((bars + 1) * OT_BAR * sampleRate) + 128, sampleRate);
      for (let n = 0; n < 16 * (bars + 1); n++) overtimeStep({ ctx, dest: ctx.destination, t: n * OT_STEP }, n, which);
      await Promise.resolve();
      const all = await ctx.startRendering();
      const from = Math.round(OT_BAR * sampleRate);
      const loop = new AudioBuffer({ length: Math.round(bars * OT_BAR * sampleRate), numberOfChannels: 1, sampleRate });
      loop.copyToChannel(all.getChannelData(0).subarray(from, from + loop.length), 0);
      return loop;
    };
    otBars = { groove: await layer("groove"), hats: await layer("hats") };
  })();
  return otBaking;
}

const hatGain = (heat: number) => (0.3 + 0.5 * heat) / 0.8;

export const overtime: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.overtime);
  let heat = 0.5;
  const crashes = (period: number) =>
    pulse(
      o,
      () => period,
      (at, n) => {
        if (n > 0 && heat > 0.7) cymbal({ ...o, t: at }, 0.45, 1.2);
      },
      12,
    );
  const bars = otBars;
  if (!bars) {
    void warmOvertime(o.ctx.sampleRate);
    const p = pulse(
      o,
      () => OT_STEP,
      (at, n) => {
        const x = { ...o, t: at };
        overtimeStep(x, n, "groove");
        overtimeStep(x, n, "hats", 0.3 + heat * 0.5);
      },
      12,
    );
    const c = crashes(OT_BAR * 4);
    return {
      stop: (t) => {
        p.stop(t);
        c.stop(t);
      },
      set: (v) => {
        heat = v;
      },
    };
  }
  const { ctx } = o;
  const groove = ctx.createBufferSource();
  groove.buffer = bars.groove;
  groove.loop = true;
  const hats = ctx.createBufferSource();
  hats.buffer = bars.hats;
  hats.loop = true;
  const hg = gain(ctx, hatGain(heat));
  groove.connect(o.dest);
  hats.connect(hg).connect(o.dest);
  groove.start(o.t);
  hats.start(o.t);
  const c = crashes(OT_BAR * 4);
  return {
    stop: (t) => {
      groove.stop(t);
      hats.stop(t);
      c.stop(t);
    },
    set: (v, t) => {
      heat = v;
      hg.gain.setTargetAtTime(hatGain(v), t, 0.2);
    },
  };
};

// ------------------------------------------------------------------ ambience

/**
 * The Foundry at rest: furnace rumble, mains hum, moving air. Distant machinery is scheduled
 * separately by the director so it can come from somewhere.
 */
export const roomTone: Build = (raw) => {
  const o = trimmed(raw, TRIM.loop.roomTone);
  const { ctx } = o;
  const rumble = noiseLoop(ctx, "brown");
  const rg = gain(ctx, 0.5);
  rumble.connect(filt(ctx, "lowpass", 170)).connect(rg).connect(o.dest);
  const swell = lfo(ctx, 0.07, 0.2, rg.gain);
  const srcs: AudioScheduledSourceNode[] = [rumble, swell];
  for (const [f, v] of [
    [50, 0.012],
    [100, 0.007],
    [150, 0.004],
  ] as const) {
    const n = osc(ctx, "sine", f);
    n.connect(gain(ctx, v)).connect(o.dest);
    srcs.push(n);
  }
  const air = noiseLoop(ctx, "pink");
  air.connect(filt(ctx, "bandpass", 3500, 0.7)).connect(gain(ctx, 0.01)).connect(o.dest);
  srcs.push(air);
  return { stop: start(o, srcs) };
};

/** One distant machine noise: a clank, a steam vent, or a chain hoist. */
export function machinery(o: Out) {
  const r = Math.random();
  if (r < 0.4) {
    thump(o, 0.4, 110, 50, 0.2);
    tone(o, { type: "square", f: 180 + Math.random() * 120, gain: 0.03, d: 0.3, lp: 900 });
    click(o, 0.2, 1500, 0.01);
    return 1.5;
  }
  if (r < 0.75) {
    const { ctx } = o;
    const src = ctx.createBufferSource();
    src.buffer = bank(ctx).white;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, o.t);
    g.gain.linearRampToValueAtTime(0.12, o.t + 0.15);
    g.gain.setTargetAtTime(0, o.t + 0.9, 0.25);
    src.loop = true;
    src.connect(filt(ctx, "bandpass", 4200, 0.8)).connect(g).connect(o.dest);
    src.start(o.t, Math.random());
    src.stop(o.t + 2.2);
    return 2.4;
  }
  for (let i = 0; i < 6; i++) click({ ...o, t: o.t + i * 0.11 }, 0.12, 900 + Math.random() * 800, 0.012);
  tone(o, { type: "sawtooth", f: 90, f1: 70, gain: 0.02, d: 0.8, lp: 400 });
  return 1.3;
}
